package com.klantenstop.web.components

import androidx.compose.runtime.Composable
import com.klantenstop.web.styles.Typography
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.GeneralKind
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.H5
import org.jetbrains.compose.web.dom.H6
import org.jetbrains.compose.web.dom.Text

/**
 * Semantic H1 heading component — renders as proper <h1> HTML element for SEO.
 * Use for main page titles/headings.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h1>` tag.
 * Override it when the document outline calls for `h1` here but a different visual weight
 * (e.g. [Typography.TitleMedium]) is wanted.
 */
@Composable
fun H1Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.HeadlineLarge,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H1(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Semantic H2 heading component — renders as proper <h2> HTML element for SEO.
 * Use for section headings and sub-headings.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h2>` tag.
 */
@Composable
fun H2Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.HeadlineMedium,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H2(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Semantic H3 heading component — renders as proper <h3> HTML element for SEO.
 * Use for sub-section headings nested under an H2.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h3>` tag.
 */
@Composable
fun H3Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.HeadlineSmall,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H3(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Semantic H4 heading component — renders as proper <h4> HTML element for SEO.
 * Use for minor headings nested under an H3.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h4>` tag.
 */
@Composable
fun H4Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.TitleLarge,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H4(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Semantic H5 heading component — renders as proper <h5> HTML element for SEO.
 * Use for minor headings nested under an H4.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h5>` tag.
 */
@Composable
fun H5Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.TitleMedium,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H5(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Semantic H6 heading component — renders as proper <h6> HTML element for SEO.
 * Use for the smallest heading level, nested under an H5.
 *
 * [typography] controls the visual look only; it is independent from the semantic `<h6>` tag.
 */
@Composable
fun H6Heading(
    modifier: Modifier = Modifier,
    typography: CssStyle<GeneralKind> = Typography.TitleSmall,
    color: CSSColorValue = MaterialColorVars.OnBackground.value(),
    text: String
) {
    H6(
        attrs = Modifier
            .margin(0.px)
            .then(typography.toModifier())
            .then(modifier)
            .color(color)
            .toAttrs()
    ) {
        Text(text)
    }
}

/**
 * Visually hidden H1 heading — renders as <h1> but is invisible to users.
 * Use on pages where design doesn't call for a visible H1, but SEO requires semantic structure.
 * Useful for search engines to understand page structure without affecting layout.
 */
val VisuallyHiddenStyle = CssStyle {
    base {
        Modifier
            .height(0.px)
            .width(0.px)
            .overflow(Overflow.Hidden)
            .position(Position.Absolute)
            .left((-9999).px)
    }
}

@Composable
fun VisuallyHiddenH1(text: String) {
    H1(
        attrs = VisuallyHiddenStyle.toModifier().toAttrs()
    ) {
        Text(text)
    }
}

